/**
 * Event Context Header - collapsible header showing selected event with stats.
 *
 * Features:
 * - Displays selected event title and statistics
 * - Collapsible/expandable with animation
 * - Event picker dropdown with search
 * - Persistence of collapsed state via sessionStorage
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  IconChevronDown,
  IconSwitch,
  IconSearch,
} from "../../../../components/Icons.tsx";
import { haptic } from "../../../../lib/telegram.ts";
import { useAdminOrdersContext, type EventOption } from "../context/index.ts";

// =============================================================================
// Constants
// =============================================================================

const COLLAPSED_STORAGE_KEY = "admin_orders_header_collapsed";

// =============================================================================
// Component
// =============================================================================

export function EventContextHeader() {
  const {
    status,
    events,
    selectedEventId,
    selectedEvent,
    setSelectedEventId,
    isLoading,
  } = useAdminOrdersContext();

  // Collapsed state with sessionStorage persistence
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = sessionStorage.getItem(COLLAPSED_STORAGE_KEY);
    return saved === "true";
  });

  // Event picker state
  const [showPicker, setShowPicker] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Persist collapsed state
  useEffect(() => {
    sessionStorage.setItem(COLLAPSED_STORAGE_KEY, String(isCollapsed));
  }, [isCollapsed]);

  // Focus search input when picker opens
  useEffect(() => {
    if (showPicker && searchInputRef.current) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [showPicker]);

  // Clear search when picker closes
  useEffect(() => {
    if (!showPicker) {
      setSearchQuery("");
    }
  }, [showPicker]);

  const toggleCollapsed = useCallback(() => {
    haptic("light");
    setIsCollapsed((prev) => !prev);
  }, []);

  const togglePicker = useCallback(() => {
    haptic("light");
    setShowPicker((prev) => !prev);
  }, []);

  const handleSelectEvent = useCallback(
    (eventId: string) => {
      haptic("light");
      setSelectedEventId(eventId);
      setShowPicker(false);
    },
    [setSelectedEventId],
  );

  // Filter events by search query
  const filteredEvents = searchQuery.trim()
    ? events.filter((e) =>
        e.title.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : events;

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="mb-4 p-3 rounded-2xl border border-slate-200 bg-white">
        <div className="h-4 w-32 bg-slate-200 rounded animate-pulse mb-3" />
        <div className="flex gap-2">
          <div className="flex-1 h-14 bg-slate-100 rounded-xl animate-pulse" />
          <div className="flex-1 h-14 bg-slate-100 rounded-xl animate-pulse" />
        </div>
      </div>
    );
  }

  // Error state - silent fail
  if (status === "error") {
    return null;
  }

  // No events
  if (events.length === 0) {
    return (
      <div className="mb-4 p-4 rounded-2xl border border-slate-200 bg-white">
        <p className="text-[13px] text-slate-500 text-center">
          Нет предстоящих событий
        </p>
      </div>
    );
  }

  const hasPending = selectedEvent && selectedEvent.pendingCount > 0;

  return (
    <div className="mb-4 rounded-2xl border border-slate-200 bg-white overflow-hidden">
      {/* Header row - always visible */}
      <div className="p-3 flex items-center justify-between">
        <div className="flex-1 min-w-0 pr-2">
          {!isCollapsed && (
            <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">
              Событие
            </div>
          )}
          <div className="text-[14px] font-semibold text-slate-900 truncate">
            {selectedEvent?.title ?? "Выберите событие"}
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          {/* Collapse/Expand button */}
          <button
            type="button"
            onClick={toggleCollapsed}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 text-slate-500 active:bg-slate-200 transition-colors"
            aria-label={isCollapsed ? "Развернуть" : "Свернуть"}
            aria-expanded={!isCollapsed}
          >
            <IconChevronDown
              className={`w-4 h-4 transition-transform duration-200 ${
                isCollapsed ? "" : "rotate-180"
              }`}
            />
          </button>

          {/* Switch event button */}
          <button
            type="button"
            onClick={togglePicker}
            className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
              showPicker
                ? "bg-[var(--color-accent)] text-white"
                : "bg-slate-100 text-slate-500 active:bg-slate-200"
            }`}
            aria-label="Сменить событие"
          >
            <IconSwitch className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Event picker dropdown */}
      {showPicker && (
        <div className="border-t border-slate-100 bg-slate-50/50">
          {/* Search input */}
          <div className="p-2 pb-0">
            <div className="relative">
              <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Поиск события..."
                className="w-full pl-9 pr-3 py-2 rounded-lg bg-white border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[var(--color-accent)]"
                autoComplete="off"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center text-xs leading-none active:opacity-80"
                  aria-label="Очистить"
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {/* Events list */}
          <div className="p-2 max-h-48 overflow-y-auto">
            {filteredEvents.length === 0 ? (
              <p className="text-[12px] text-slate-400 text-center py-3">
                Ничего не найдено
              </p>
            ) : (
              filteredEvents.map((event) => (
                <EventPickerItem
                  key={event.id}
                  event={event}
                  isSelected={event.id === selectedEventId}
                  onSelect={handleSelectEvent}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* Stats row - only when expanded and event selected */}
      {!isCollapsed && selectedEvent && (
        <div className="px-3 pb-3">
          <div className="flex gap-2">
            <div className="flex-1 py-2 px-3 rounded-xl bg-slate-50">
              <div className="text-lg font-bold text-slate-800">
                {selectedEvent.ordersCount}
              </div>
              <div className="text-[10px] text-slate-500">заказов</div>
            </div>

            <div className="flex-1 py-2 px-3 rounded-xl bg-teal-50 border border-teal-100">
              <div className="text-lg font-bold text-teal-700">
                {selectedEvent.ticketsCount}
              </div>
              <div className="text-[10px] text-teal-500">билетов</div>
            </div>

            {hasPending && (
              <Link
                to="/admin/payments"
                className="flex-1 py-2 px-3 rounded-xl bg-amber-50 border border-amber-200 active:opacity-80 transition-opacity"
              >
                <div className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                  <span className="text-lg font-bold text-amber-700">
                    {selectedEvent.pendingCount}
                  </span>
                </div>
                <div className="text-[10px] text-amber-600">на проверке</div>
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Event Picker Item
// =============================================================================

interface EventPickerItemProps {
  event: EventOption;
  isSelected: boolean;
  onSelect: (eventId: string) => void;
}

function EventPickerItem({
  event,
  isSelected,
  onSelect,
}: EventPickerItemProps) {
  return (
    <button
      type="button"
      onClick={() => onSelect(event.id)}
      className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
        isSelected
          ? "bg-[var(--color-accent)]/10 border border-[var(--color-accent)]/30"
          : "active:bg-slate-100"
      }`}
    >
      <div
        className={`text-[13px] font-medium truncate ${
          isSelected ? "text-[var(--color-accent)]" : "text-slate-900"
        }`}
      >
        {event.title}
      </div>
      <div className="text-[11px] text-slate-500">
        {event.ordersCount} заказов · {event.ticketsCount} билетов
        {event.pendingCount > 0 && (
          <span className="text-amber-600"> · {event.pendingCount} pending</span>
        )}
      </div>
    </button>
  );
}
